import { Utils } from '@common';
import { React, Styled } from '@common/3rd';
import { DeskComponentRegistry, ModelContainerWidget, ModelWidget, getAllRuntimeComponents } from '@component';
import {
	DeskButtonComponents,
	DeskButtonGroupComponents,
	DeskButtonProps,
	DeskLinkComponents,
	DeskLinkProps
} from '@my-types';
import { StyledComponents } from '@styles';


const CARED = ['DeskButton', 'DeskLink'];
const handleMultipleClick = (invoker: any, disabled: boolean): void => {
	getAllRuntimeComponents()
		.filter(child => {
			let name = child.constructor && child.constructor.name;
			let prototype = (child as any).__proto__;
			while (true) {
				if (CARED.includes(name)) {
					return true;
				} else if (name === 'ModelWidget') {
					break;
				} else if (prototype == null) {
					break;
				} else if (prototype.constructor == null) {
					break;
				} else {
					name = prototype.constructor.name;
					prototype = prototype.__proto__;
				}
			}
			return false;
		})
		.forEach((comp: any) => comp.setDisabled && comp.setDisabled(disabled));
};
const avoidMultipleClick = (invoker: any | null): void => {
	handleMultipleClick(invoker, true);
};
const recoverMultipleClick = (invoker: any): void => {
	handleMultipleClick(invoker, false);
};

export type DeskButtonState = { disabled?: boolean; intervalLeft: number };

/**
 * 标准桌面按钮
 */
class DeskButton<P extends DeskButtonProps, S extends DeskButtonState, C extends DeskButtonComponents> extends ModelWidget<P, S, C> {
	internalTimeout?: any;

	componentDidMount() {
		super.componentDidMount();
		this.intervalClick();
	}

	componentWillUnmount() {
		super.componentWillUnmount();
		this.clearIntervalTimeout();
	}

	protected initComponents(): C {
		return {
			Box: Styled.div`
				display: flex;
			`,
			Button: Styled(StyledComponents.BUTTON).attrs({
				'data-widget': 'desk-btn'
			})`
				&[data-interval=true] {
					background-color: ${props => props.theme.FONT_COLOR_LIGHT2};
				}
				min-width: ${props => props.theme.DESK_BTN_MIN_WIDTH}px;
				${props => this.renderCSS(props.theme)}
				&[disabled] {
					opacity: .5;
				}
			`,
			Desc: Styled.div`
				margin-left: 15px;
			`
		} as C;
	}
	render() {
		const C = this.getComponents();
		return (
			this.props.desc ?
			<C.Box>
				<C.Button 
					type={this.props.type} 
					onClick={this.onClicked as any}
					disabled={this.props.disabled || this.isDisabled()}
					data-interval={this.state.intervalLeft > 0}>
					{this.getLabel()}
					{this.renderInterval()}
				</C.Button>
				<C.Desc>{this.props.desc}</C.Desc>
			</C.Box> : 
			<C.Button 
				type={this.props.type} 
				onClick={this.onClicked as any}
				disabled={this.props.disabled || this.isDisabled()}
				data-interval={this.state.intervalLeft > 0}>
				{this.getLabel()}
				{this.renderInterval()}
			</C.Button>
		);
	}
	protected getLabel(): any {
		return this.props.label || this.props.children;
	}
	protected isDisabled(): boolean {
		return this.state.disabled === true;
	}

	protected setDisabled(disabled: boolean): void {
		this.setState({ disabled });
	}
	protected onClicked = (evt: MouseEvent): void => {
		if (!this.props.disabled && !this.isDisabled() && this.props.onClick && !this.state.intervalLeft) {
			const interval = this.getNextClickInterval();
			if (interval !== -1) {
				this.setState({ intervalLeft: interval });
				this.startInterval();
			}

			avoidMultipleClick(this);

			const ret = this.props.onClick.call(this, this, evt);
			if (Utils.isPromise(ret)) {
				(ret as any)
					.then(() => recoverMultipleClick(this))
					.catch(() => recoverMultipleClick(this));
			} else {
				recoverMultipleClick(this);
			}
		}
	};

	protected renderInterval(): string {
		const interval = this.getNextClickInterval();
		const intervalLeft = this.state.intervalLeft;
		if (interval === -1) {
			return '';
		} else if (intervalLeft) {
			return ` (${intervalLeft}秒)`;
		} else {
			return '';
		}
	}

	//倒计时
	intervalClick() {
		//启用了自动倒计时
		if (this.props.interval != null && this.props.interval > 0 && this.autoInterval()) {
			this.setState({ intervalLeft: this.getNextClickInterval() + 1 as number }, () => this.interval());
		}
	}

	interval() {
		if (this.state.intervalLeft > 0) {
			this.clearIntervalTimeout();
			this.setState({
				intervalLeft: this.state.intervalLeft - 1 as number
			}, () => {
				if (this.state.intervalLeft > 0) {
					// 间隔时间之后再次发起轮询
					this.internalTimeout = setTimeout(() => this.interval(), 1000);
				} else {
					//结束，不在处理
					this.clearIntervalTimeout();
				}
			});
		}
	}


	private clearIntervalTimeout() {
		if (this.internalTimeout) {
			clearTimeout(this.internalTimeout);
			delete this.internalTimeout;
		}
	}

	protected getNextClickInterval(): number {
		return this.props.interval == null ? -1 : this.props.interval!;
	}

	protected startInterval(): void {
		setTimeout(() => {
			const left = this.state.intervalLeft - 1;
			this.setState({ intervalLeft: left });
			if (left !== 0) {
				this.startInterval();
			}
		}, 1000);
	}

	/**
	 * 是否页面加载就自动倒计时,不写默认是false
	 */
	protected autoInterval(): boolean {
		return this.props.autoInterval === true;
	}
}

/**
 * 标准桌面链接
 */
class DeskLink<P extends DeskLinkProps, S, C extends DeskLinkComponents> extends ModelWidget<P, S, C> {
	protected initComponents(): C {
		return {
			Link: Styled(StyledComponents.LINK).attrs({
				'data-widget': 'desk-link'
			})`
				min-width: ${props => props.theme.DESK_BTN_MIN_WIDTH}px;
				${props => this.renderCSS(props.theme)}
			`
		} as C;
	}
	render() {
		const C = this.getComponents();
		return <C.Link onClick={this.onClicked as any}>{this.getLabel()}</C.Link>;
	}
	getLabel() {
		return this.props.label;
	}
	onClicked = (evt: MouseEvent) => {
		if (this.props.onClick) {
			this.props.onClick.call(this, this, evt);
		}
	};
}

class DeskButtonGroup<P, S, C extends DeskButtonGroupComponents> extends ModelContainerWidget<P, S, C> {
	protected initComponents(): C {
		return {
			Box: Styled.div.attrs({
				'data-widget': 'desk-btn-group'
			})`
				display: flex;
				> a {
					&:not(:first-child) {
						margin-left: ${props => props.theme.BTN_GROUP_GAP}px;
					}
				}
				${props => this.renderCSS(props.theme)}
			`
		} as C;
	}
	render() {
		const C = this.getComponents();
		return <C.Box>{this.renderChildren()}</C.Box>;
	}
}

DeskComponentRegistry.register({
	'button-group': DeskButtonGroup,
	button: DeskButton,
	link: DeskLink
});

export { DeskButton, DeskLink, DeskButtonGroup };
